import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../config/axios";
import { FERIADOS_SIN_PERSONAL } from "./cleaningConfig";
import type { GetMotivoDiaSinPersonal } from "./limpiezaUtils";

export const CLEANING_SPECIAL_DAYS_QUERY_KEY = ["cleaning-special-days"] as const;

export interface CleaningSpecialDay {
  id: number;
  fecha: string;
  hay_personal: boolean;
  nota: string;
}

interface CleaningSpecialDaysResponse {
  dias: CleaningSpecialDay[];
}

export async function fetchCleaningSpecialDays(): Promise<CleaningSpecialDay[]> {
  const response = await api.get<CleaningSpecialDaysResponse>("/limpieza/dias-especiales");
  return response.data.dias || [];
}

export async function saveCleaningSpecialDay(payload: {
  fecha: string;
  hay_personal: boolean;
  nota: string;
}): Promise<CleaningSpecialDay> {
  const response = await api.post<{ dia: CleaningSpecialDay }>("/limpieza/dias-especiales", payload);
  return response.data.dia;
}

export async function deleteCleaningSpecialDay(fecha: string): Promise<void> {
  await api.delete(`/limpieza/dias-especiales/${fecha}`);
}

export function buildMotivoDiaSinPersonal(
  specialDays: CleaningSpecialDay[] = []
): GetMotivoDiaSinPersonal {
  const specialDaysByDate = new Map(specialDays.map((day) => [day.fecha, day]));

  return (fecha) => {
    const especial = specialDaysByDate.get(fecha);
    if (especial) return especial.hay_personal ? null : "feriado";
    if (FERIADOS_SIN_PERSONAL.includes(fecha)) return "feriado";
    return new Date(`${fecha}T00:00:00`).getDay() === 0 ? "domingo" : null;
  };
}

export function useCleaningSpecialDaysReason() {
  const specialDaysQuery = useQuery({
    queryKey: CLEANING_SPECIAL_DAYS_QUERY_KEY,
    queryFn: fetchCleaningSpecialDays,
    staleTime: 5 * 60 * 1000,
  });

  const specialDays = specialDaysQuery.data || [];
  const getMotivoDiaSinPersonal = useMemo(
    () => buildMotivoDiaSinPersonal(specialDays),
    [specialDays]
  );

  return { specialDays, specialDaysQuery, getMotivoDiaSinPersonal };
}
